/**
 * TrustDetails.jsx — private-trust particulars and this year's trust data.
 *
 * A private trust's statements need facts a company's do not: the deed and Master's
 * reference, the trustees and beneficiaries, what was distributed (income vs capital,
 * paid vs vested-unpaid), how much of any capital gain vested, and the terms of loans
 * with trustees / founder / beneficiaries (section 7C). None of it can be inferred from a
 * trial balance, so it is captured here and used by the validation gate, the trust notes
 * (25–27), the tax note and the Trustees' Report. Nothing entered here is invented by AI.
 *
 * Route: /trust-details. Expects the same location.state the Notes page receives
 * (client, financial_year, ...) plus an optional `returnTo` route.
 */
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getTrustData, saveTrustData } from '../api/kailux'
import Icon from '../components/Icon'
import { T, alpha } from '../theme/theme'

const TRUST_TYPES = [['', 'Not recorded'], ['inter_vivos', 'Inter vivos (living trust)'], ['testamentary', 'Testamentary (will)']]
const VESTINGS = [['', 'Not recorded'], ['discretionary', 'Discretionary'], ['vesting', 'Vesting'], ['bewind', 'Bewind'], ['hybrid', 'Hybrid']]
const CAPACITIES = [['', '—'], ['founder', 'Founder'], ['independent', 'Independent'], ['beneficiary', 'Beneficiary'], ['professional', 'Professional'], ['other', 'Other']]

const input = { width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${T.bd}`, background: T.s2, color: T.tx, fontSize: 13, boxSizing: 'border-box' }
const card = { background: T.s1, border: `1px solid ${T.bd}`, borderRadius: 12, padding: 20, marginBottom: 18 }
const h2 = { fontSize: 15, fontWeight: 700, margin: '0 0 4px' }
const hint = { fontSize: 12, color: T.mt, margin: '0 0 14px' }
const btn = { padding: '8px 14px', borderRadius: 8, border: `1px solid ${T.bd}`, background: 'transparent', color: T.tx, fontSize: 13, cursor: 'pointer' }
const primary = { ...btn, background: T.prg, color: T.onpri, border: 'none', fontWeight: 600 }

const money = (n) => `R${(Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const num = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : 0 }

function Field({ label, children, wide }) {
  return (
    <label style={{ display: 'block', gridColumn: wide ? '1 / -1' : undefined }}>
      <span style={{ display: 'block', fontSize: 12, color: T.mt, marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  )
}

function Select({ value, onChange, options, ...rest }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={input} {...rest}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  )
}

export default function TrustDetails() {
  const location = useLocation()
  const navigate = useNavigate()
  const st = location.state || {}
  const client = st.client
  const financialYear = String(st.financial_year || '')

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedAt, setSavedAt] = useState(null)
  const [details, setDetails] = useState({ trust_type: '', vesting: '', master_office: '', master_ref: '', deed_date: '', founder: '', special_trust: false, trustees: [], beneficiaries: [], objects: '' })
  const [year, setYear] = useState({ income_distributions: [], capital_distributions: [], net_capital_gain: '', capital_gain_vested: '', beneficiary_loans: [], year_notes: '' })

  useEffect(() => {
    let live = true
    if (!client?.id || !financialYear) { setLoading(false); return () => {} }
    getTrustData(client.id, financialYear)
      .then((d) => {
        if (!live) return
        setDetails(d.trust.details)
        const y = d.trust.year
        setYear({ ...y, net_capital_gain: y.net_capital_gain || '', capital_gain_vested: y.capital_gain_vested || '',
          beneficiary_loans: y.beneficiary_loans.map((l) => ({ ...l, interest_rate: l.interest_rate == null ? '' : +(l.interest_rate * 100).toFixed(4), official_rate: l.official_rate == null ? '' : +(l.official_rate * 100).toFixed(4) })) })
      })
      .catch((e) => live && setError(e?.response?.data?.detail || 'Could not load trust data.'))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [client?.id, financialYear])

  const totals = useMemo(() => {
    const inc = year.income_distributions.reduce((s, r) => s + num(r.amount), 0)
    const cap = year.capital_distributions.reduce((s, r) => s + num(r.amount), 0)
    const unpaid = [...year.income_distributions, ...year.capital_distributions].filter((r) => r.status === 'vested_unpaid').reduce((s, r) => s + num(r.amount), 0)
    const tb = Number(st.figures?.['Distributions to Beneficiaries'] || 0)
    return { inc, cap, total: inc + cap, unpaid, tb }
  }, [year, st.figures])

  const setList = (setter, key) => ({
    add: (row) => setter((p) => ({ ...p, [key]: [...p[key], row] })),
    upd: (i, patch) => setter((p) => ({ ...p, [key]: p[key].map((r, j) => (j === i ? { ...r, ...patch } : r)) })),
    del: (i) => setter((p) => ({ ...p, [key]: p[key].filter((_, j) => j !== i) })),
  })
  const trustees = setList(setDetails, 'trustees')
  const beneficiaries = setList(setDetails, 'beneficiaries')
  const incD = setList(setYear, 'income_distributions')
  const capD = setList(setYear, 'capital_distributions')
  const loans = setList(setYear, 'beneficiary_loans')

  async function save(thenReturn) {
    setSaving(true); setError('')
    try {
      const payload = {
        details,
        year: {
          ...year,
          net_capital_gain: num(year.net_capital_gain), capital_gain_vested: num(year.capital_gain_vested),
          beneficiary_loans: year.beneficiary_loans.map((l) => ({ ...l, balance: num(l.balance), interest_rate: l.interest_rate === '' ? null : +(num(l.interest_rate) / 100).toFixed(6), official_rate: l.official_rate === '' ? null : +(num(l.official_rate) / 100).toFixed(6) })),
        },
      }
      await saveTrustData(client.id, financialYear, payload)
      setSavedAt(new Date())
      if (thenReturn) navigate(st.returnTo || '/notes', { state: { ...st, returnTo: undefined } })
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save trust data.')
    } finally { setSaving(false) }
  }

  if (!client?.id) {
    return <div style={{ padding: 40, color: T.tx, background: T.bg, minHeight: '100vh' }}>Open this page from a trust client's statements flow.
      <div><button style={btn} onClick={() => navigate('/clients')}>Back to clients</button></div></div>
  }

  const distRows = (list, ctl, nature) => (
    <>
      {list.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.4fr auto', gap: 8, marginBottom: 8 }}>
          <input aria-label={`${nature} beneficiary ${i + 1}`} style={input} placeholder="Beneficiary" value={r.beneficiary || ''} onChange={(e) => ctl.upd(i, { beneficiary: e.target.value })} />
          <input aria-label={`${nature} amount ${i + 1}`} style={input} inputMode="decimal" placeholder="Amount" value={r.amount ?? ''} onChange={(e) => ctl.upd(i, { amount: e.target.value })} />
          <Select aria-label={`${nature} status ${i + 1}`} value={r.status || 'paid'} onChange={(v) => ctl.upd(i, { status: v })} options={[['paid', 'Paid in cash'], ['vested_unpaid', 'Vested, unpaid']]} />
          <button type="button" style={btn} aria-label={`Remove ${nature} distribution ${i + 1}`} onClick={() => ctl.del(i)}><Icon name="x" /></button>
        </div>
      ))}
      <button type="button" style={btn} onClick={() => ctl.add({ beneficiary: '', amount: '', status: 'paid' })}>+ Add {nature} distribution</button>
    </>
  )

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.tx, fontFamily: "'Outfit',sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px 80px' }}>
        <button type="button" style={{ ...btn, marginBottom: 16 }} onClick={() => navigate(st.returnTo || '/notes', { state: { ...st, returnTo: undefined } })}>← Back</button>
        <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>Trust details — {client.name}</h1>
        <p style={{ ...hint, marginBottom: 20 }}>Financial year {financialYear}. These facts appear in the trustees' report, the trust notes and the tax note. Anything left blank is shown in the statements as "to be confirmed" — KaiLux never fills it in for you.</p>

        {error && <div role="alert" style={{ background: alpha(T.rd, 0.12), border: `1px solid ${alpha(T.rd, 0.4)}`, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>{error}</div>}
        {loading ? <p>Loading…</p> : (
          <>
            <section style={card} aria-label="Trust particulars">
              <h2 style={h2}>Trust particulars</h2>
              <p style={hint}>From the trust deed and the Master's letters of authority.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Type of trust"><Select value={details.trust_type} onChange={(v) => setDetails({ ...details, trust_type: v })} options={TRUST_TYPES} /></Field>
                <Field label="Beneficial interest"><Select value={details.vesting} onChange={(v) => setDetails({ ...details, vesting: v })} options={VESTINGS} /></Field>
                <Field label="Master's reference number"><input style={input} value={details.master_ref} onChange={(e) => setDetails({ ...details, master_ref: e.target.value })} placeholder="e.g. IT1234/2015" /></Field>
                <Field label="Master's Office"><input style={input} value={details.master_office} onChange={(e) => setDetails({ ...details, master_office: e.target.value })} placeholder="e.g. Pretoria" /></Field>
                <Field label="Date of trust deed"><input style={input} value={details.deed_date} onChange={(e) => setDetails({ ...details, deed_date: e.target.value })} placeholder="e.g. 5 May 2015" /></Field>
                <Field label="Founder / donor"><input style={input} value={details.founder} onChange={(e) => setDetails({ ...details, founder: e.target.value })} /></Field>
                <Field label="Objects of the trust" wide><textarea style={{ ...input, minHeight: 60 }} value={details.objects} onChange={(e) => setDetails({ ...details, objects: e.target.value })} /></Field>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 13 }}>
                <input type="checkbox" checked={!!details.special_trust} onChange={(e) => setDetails({ ...details, special_trust: e.target.checked })} />
                This is a special trust (taxed on the sliding scale — you will confirm the rate on the tax step)
              </label>
            </section>

            <section style={card} aria-label="Trustees">
              <h2 style={h2}>Trustees</h2>
              {details.trustees.map((t, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 1.5fr auto', gap: 8, marginBottom: 8 }}>
                  <input aria-label={`Trustee name ${i + 1}`} style={input} value={t.name} onChange={(e) => trustees.upd(i, { name: e.target.value })} placeholder="Full name" />
                  <Select aria-label={`Trustee capacity ${i + 1}`} value={t.capacity || ''} onChange={(v) => trustees.upd(i, { capacity: v })} options={CAPACITIES} />
                  <button type="button" style={btn} aria-label={`Remove trustee ${i + 1}`} onClick={() => trustees.del(i)}><Icon name="x" /></button>
                </div>
              ))}
              <button type="button" style={btn} onClick={() => trustees.add({ name: '', capacity: '' })}>+ Add trustee</button>
            </section>

            <section style={card} aria-label="Beneficiaries">
              <h2 style={h2}>Beneficiaries</h2>
              {details.beneficiaries.map((b, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '3fr 2fr auto', gap: 8, marginBottom: 8 }}>
                  <input aria-label={`Beneficiary name ${i + 1}`} style={input} value={b.name} onChange={(e) => beneficiaries.upd(i, { name: e.target.value })} placeholder="Full name" />
                  <input aria-label={`Beneficiary relationship ${i + 1}`} style={input} value={b.relationship || ''} onChange={(e) => beneficiaries.upd(i, { relationship: e.target.value })} placeholder="Relationship (optional)" />
                  <button type="button" style={btn} aria-label={`Remove beneficiary ${i + 1}`} onClick={() => beneficiaries.del(i)}><Icon name="x" /></button>
                </div>
              ))}
              <button type="button" style={btn} onClick={() => beneficiaries.add({ name: '', relationship: '' })}>+ Add beneficiary</button>
            </section>

            <section style={card} aria-label="Distributions">
              <h2 style={h2}>Distributions to beneficiaries</h2>
              <p style={hint}>Split what the trial balance shows as distributions into income and capital. Income that vests in a beneficiary in the year is taxed in their hands (the conduit principle), so this split drives the trust's own tax.</p>
              <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Income distributions</h3>
              {distRows(year.income_distributions, incD, 'income')}
              <h3 style={{ fontSize: 13, margin: '16px 0 8px' }}>Capital distributions</h3>
              {distRows(year.capital_distributions, capD, 'capital')}
              <div data-testid="dist-summary" style={{ marginTop: 14, fontSize: 13, color: Math.abs(totals.total - totals.tb) < 1 || (!totals.total && !totals.tb) ? T.lt : T.yl }}>
                Itemised: {money(totals.total)} (income {money(totals.inc)}, capital {money(totals.cap)}; vested but unpaid {money(totals.unpaid)}) · Trial balance: {money(totals.tb)}
                {totals.total > 0 && Math.abs(totals.total - totals.tb) >= 1 && <strong> — these must agree before the statements are issued.</strong>}
              </div>
            </section>

            <section style={card} aria-label="Capital gains">
              <h2 style={h2}>Capital gains</h2>
              <p style={hint}>Only if the trust disposed of capital assets. A gain retained in the trust is taxed at an effective 36%; a gain that vests in a beneficiary is taxed there.</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Net capital gain for the year (R) — the gain already included in profit before tax"><input style={input} inputMode="decimal" value={year.net_capital_gain} onChange={(e) => setYear({ ...year, net_capital_gain: e.target.value })} /></Field>
                <Field label="Of which vested in beneficiaries (R)"><input style={input} inputMode="decimal" value={year.capital_gain_vested} onChange={(e) => setYear({ ...year, capital_gain_vested: e.target.value })} /></Field>
              </div>
            </section>

            <section style={card} aria-label="Loan accounts">
              <h2 style={h2}>Loans with trustees, the founder and beneficiaries</h2>
              <p style={hint}>Section 7C of the Income Tax Act treats the shortfall between the official rate and the interest charged on a connected person's loan <em>to</em> a trust as a donation each year. Enter the official rate for the year if you want KaiLux to compute it — it is never assumed.</p>
              {year.beneficiary_loans.map((l, i) => (
                <div key={i} style={{ border: `1px solid ${T.bd}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1fr', gap: 8, marginBottom: 8 }}>
                    <input aria-label={`Loan party ${i + 1}`} style={input} placeholder="Name" value={l.name} onChange={(e) => loans.upd(i, { name: e.target.value })} />
                    <Select aria-label={`Loan direction ${i + 1}`} value={l.direction} onChange={(v) => loans.upd(i, { direction: v })} options={[['from', 'Owed BY the trust to them'], ['to', 'Owed TO the trust by them']]} />
                    <input aria-label={`Loan balance ${i + 1}`} style={input} inputMode="decimal" placeholder="Balance (R)" value={l.balance ?? ''} onChange={(e) => loans.upd(i, { balance: e.target.value })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={!!l.interest_free} onChange={(e) => loans.upd(i, { interest_free: e.target.checked })} /> Interest free</label>
                    <input aria-label={`Loan interest rate ${i + 1}`} style={input} inputMode="decimal" placeholder="Interest % p.a." disabled={!!l.interest_free} value={l.interest_rate ?? ''} onChange={(e) => loans.upd(i, { interest_rate: e.target.value })} />
                    <input aria-label={`Official rate ${i + 1}`} style={input} inputMode="decimal" placeholder="s7C official rate %" value={l.official_rate ?? ''} onChange={(e) => loans.upd(i, { official_rate: e.target.value })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr auto auto', gap: 8, alignItems: 'center' }}>
                    <input aria-label={`Repayment terms ${i + 1}`} style={input} placeholder="Repayment terms (e.g. repayable on demand)" value={l.repayment_terms || ''} onChange={(e) => loans.upd(i, { repayment_terms: e.target.value })} />
                    <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={l.connected_person !== false} onChange={(e) => loans.upd(i, { connected_person: e.target.checked })} /> Connected person</label>
                    <button type="button" style={btn} aria-label={`Remove loan ${i + 1}`} onClick={() => loans.del(i)}><Icon name="x" /></button>
                  </div>
                </div>
              ))}
              <button type="button" style={btn} onClick={() => loans.add({ name: '', direction: 'from', balance: '', interest_free: false, interest_rate: '', official_rate: '', repayment_terms: '', connected_person: true })}>+ Add loan</button>
            </section>

            <section style={card} aria-label="Year notes">
              <h2 style={h2}>Additional trustees' report matters</h2>
              <textarea aria-label="Year notes" style={{ ...input, minHeight: 70 }} value={year.year_notes} onChange={(e) => setYear({ ...year, year_notes: e.target.value })} placeholder="Anything the trustees want disclosed for this year (optional)" />
            </section>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button type="button" style={primary} disabled={saving} onClick={() => save(true)}>{saving ? 'Saving…' : 'Save and continue'}</button>
              <button type="button" style={btn} disabled={saving} onClick={() => save(false)}>Save</button>
              {savedAt && <span style={{ fontSize: 12, color: T.gn }}><Icon name="check" /> Saved</span>}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
